import type { OrgRow } from '~/gql/organizations/organizations'
import { useMutation, useQuery } from '@apollo/client/react'
import { BillingMode, OrganizationStatus } from '@zeus/index'
import { MoreHorizontal } from 'lucide-react'
import { useState } from 'react'
import { useForm } from 'react-hook-form'
import { useTranslation } from 'react-i18next'
import { toast } from 'sonner'
import FieldError from '~/components/FieldError'
import RequiredFieldsHint from '~/components/RequiredFieldsHint'
import { SidePanel } from '~/components/SidePanel'
import { Badge } from '~/components/ui/badge'
import { Button } from '~/components/ui/button'
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from '~/components/ui/dropdown-menu'
import { Input } from '~/components/ui/input'
import { Label } from '~/components/ui/label'
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '~/components/ui/table'
import {
  GQL_CREATE_ORGANIZATION,
  GQL_ORGANIZATIONS,
  GQL_SET_ORGANIZATION_STATUS,
} from '~/gql/organizations/organizations'
import { client } from '~/helpers/apollo'
import { errorAria } from '~/helpers/forms/error-aria'
import { tourAnchor } from '~/helpers/tour/anchors'
import OrganizationDeleteModal from '~/pages/backoffice/admin/organizations/components/OrganizationDeleteModal'
import OrganizationEditModal from '~/pages/backoffice/admin/organizations/components/OrganizationEditModal'
import OrganizationMembersModal from '~/pages/backoffice/admin/organizations/components/OrganizationMembersModal'

type FormData = {
  name: string
  slug: string
  adminEmail: string
  adminFirstName: string
  adminLastName: string
}

function OrganizationsTable() {
  const { t } = useTranslation()
  const [open, setOpen] = useState(false)
  const [editing, setEditing] = useState<OrgRow | null>(null)
  const [membersFor, setMembersFor] = useState<OrgRow | null>(null)
  const [deleting, setDeleting] = useState<OrgRow | null>(null)

  const { data, refetch } = useQuery(GQL_ORGANIZATIONS, { client, fetchPolicy: 'network-only' })
  const [createOrganization, { loading }] = useMutation(GQL_CREATE_ORGANIZATION, { client })
  const [setStatus] = useMutation(GQL_SET_ORGANIZATION_STATUS, { client })

  const { register, handleSubmit, reset, watch, setValue, formState: { errors } } = useForm<FormData>({
    defaultValues: { name: '', slug: '', adminEmail: '', adminFirstName: '', adminLastName: '' },
  })

  const organizations = (data?.organizations ?? []) as unknown as OrgRow[]

  const onCreate = handleSubmit(async (values) => {
    try {
      await createOrganization({ variables: { input: values } })
      toast.success(t('good.add.organization'))
      setOpen(false)
      reset()
      await refetch()
    } catch {
      toast.error(t('bad.add.organization'))
    }
  })

  const toggleStatus = async (org: OrgRow) => {
    const status = org.status === OrganizationStatus.ACTIVE ? OrganizationStatus.SUSPENDED : OrganizationStatus.ACTIVE
    try {
      await setStatus({ variables: { id: org.id, status } })
      await refetch()
    } catch {
      toast.error(t('unknow.error'))
    }
  }

  return (
    <div className="space-y-4">
      <div className="flex flex-wrap items-center justify-end gap-3">
        <Button onClick={() => setOpen(true)} {...tourAnchor('organizations.create')}>{t('organization.create')}</Button>
      </div>

      <div className="rounded-md border" {...tourAnchor('organizations.list')}>
        <Table aria-label={t('common.routing.organizations')}>
          <TableHeader>
            <TableRow>
              <TableHead>{t('common.name')}</TableHead>
              <TableHead>{t('organization.slug')}</TableHead>
              <TableHead>{t('organization.members')}</TableHead>
              <TableHead>{t('organization.edit.tier')}</TableHead>
              <TableHead>{t('common.status')}</TableHead>
              <TableHead className="w-[80px] text-right">{t('common.action')}</TableHead>
            </TableRow>
          </TableHeader>
          <TableBody>
            {organizations.map(org => (
              <TableRow key={org.id}>
                <TableCell className="font-medium">{org.name}</TableCell>
                <TableCell className="text-muted-foreground">{org.slug}</TableCell>
                <TableCell>{org.userCount}</TableCell>
                <TableCell>{org.billingMode === BillingMode.MANAGED ? t('billing.mode.MANAGED') : t(`billing.tier.${org.tier}`)}</TableCell>
                <TableCell>
                  <Badge variant={org.status === OrganizationStatus.ACTIVE ? 'success' : 'secondary'}>
                    {org.status === OrganizationStatus.ACTIVE ? t('status.active') : t('organization.suspended')}
                  </Badge>
                </TableCell>
                <TableCell className="text-right">
                  <DropdownMenu>
                    <DropdownMenuTrigger asChild>
                      <Button variant="ghost" className="size-8 p-0" {...tourAnchor('organizations.actions')}>
                        <span className="sr-only">{t('common.actionFor', { action: t('common.action'), name: org.name })}</span>
                        <MoreHorizontal className="size-4" />
                      </Button>
                    </DropdownMenuTrigger>
                    <DropdownMenuContent align="end">
                      <DropdownMenuItem onClick={() => setEditing(org)}>
                        {t('common.update')}
                      </DropdownMenuItem>
                      <DropdownMenuItem onClick={() => setMembersFor(org)}>
                        {t('organization.members')}
                      </DropdownMenuItem>
                      <DropdownMenuItem onClick={() => toggleStatus(org)}>
                        {org.status === OrganizationStatus.ACTIVE ? t('organization.suspend') : t('organization.activate')}
                      </DropdownMenuItem>
                      <DropdownMenuItem
                        className="text-destructive focus:text-destructive"
                        onClick={() => setDeleting(org)}
                      >
                        {t('common.delete')}
                      </DropdownMenuItem>
                    </DropdownMenuContent>
                  </DropdownMenu>
                </TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </div>

      {/* Création */}
      <SidePanel
        open={open}
        onOpenChange={setOpen}
        title={t('organization.create')}
        // Le pied du tiroir est hors du <form> : le bouton s'y rattache par son id.
        footer={(
          <>
            <Button variant="outline" type="button" onClick={() => setOpen(false)}>
              {t('common.cancel')}
            </Button>
            <Button type="submit" form="organization-form" disabled={loading}>{t('common.create')}</Button>
          </>
        )}
      >
        <form id="organization-form" onSubmit={onCreate} className="space-y-4">
          <RequiredFieldsHint />
          <p className="text-sm text-muted-foreground">{t('organization.create.help')}</p>
          <div className="space-y-2">
            <Label htmlFor="name" required>{t('common.name')}</Label>
            <Input id="name" aria-required {...errorAria('name', errors.name)} {...register('name', { required: t('input.required.message') })} />
            <FieldError fieldId="name" message={errors.name?.message} />
          </div>
          <div className="space-y-2">
            <Label htmlFor="slug" required>{t('organization.slug')}</Label>
            <Input
              id="slug"
              aria-required
              {...errorAria('slug', errors.slug)}
              {...register('slug', { required: t('input.required.message') })}
              onChange={e => setValue('slug', e.target.value.toLowerCase().replace(/[^a-z0-9-]/g, '-'))}
              value={watch('slug')}
            />
            <FieldError fieldId="slug" message={errors.slug?.message} />
          </div>
          <div className="space-y-2">
            <Label htmlFor="adminEmail" required>{t('organization.admin.email')}</Label>
            <Input id="adminEmail" aria-required {...errorAria('adminEmail', errors.adminEmail)} type="email" {...register('adminEmail', { required: t('input.required.message') })} />
            <FieldError fieldId="adminEmail" message={errors.adminEmail?.message} />
          </div>
          <div className="grid grid-cols-2 gap-2">
            <div className="space-y-2">
              <Label htmlFor="adminFirstName" required>{t('common.firstname')}</Label>
              <Input id="adminFirstName" aria-required {...errorAria('adminFirstName', errors.adminFirstName)} {...register('adminFirstName', { required: t('input.required.message') })} />
              <FieldError fieldId="adminFirstName" message={errors.adminFirstName?.message} />
            </div>
            <div className="space-y-2">
              <Label htmlFor="adminLastName" required>{t('common.lastname')}</Label>
              <Input id="adminLastName" aria-required {...errorAria('adminLastName', errors.adminLastName)} {...register('adminLastName', { required: t('input.required.message') })} />
              <FieldError fieldId="adminLastName" message={errors.adminLastName?.message} />
            </div>
          </div>
        </form>
      </SidePanel>

      {editing && (
        <OrganizationEditModal
          open={!!editing}
          onCancel={() => setEditing(null)}
          onSuccess={refetch}
          org={editing}
        />
      )}

      {membersFor && (
        <OrganizationMembersModal
          open={!!membersFor}
          onCancel={() => setMembersFor(null)}
          onChanged={refetch}
          org={membersFor}
        />
      )}

      {deleting && (
        <OrganizationDeleteModal
          open={!!deleting}
          onCancel={() => setDeleting(null)}
          onSuccess={refetch}
          org={deleting}
        />
      )}
    </div>
  )
}

export default OrganizationsTable
